import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Brain, Flame, Droplets } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { articleSchema, breadcrumbSchema, localizedHead, medicalWebPageSchema, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";
import { HeroImage } from "@/components/hero-image";
import { scienceAbstractImage } from "@/lib/responsive-images";

export const Route = createFileRoute("/wissenschaft")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => ({
    ...localizedHead("/wissenschaft", loaderData?.lang),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/wissenschaft", loaderData?.lang)) },
      { type: "application/ld+json", children: JSON.stringify(medicalWebPageSchema("/wissenschaft", loaderData?.lang, { specialty: "Pharmacologic", aspect: "Overview" })) },
      { type: "application/ld+json", children: JSON.stringify(articleSchema("/wissenschaft", loaderData?.lang, { section: "Science" })) },
    ],
  }),
  component: Page,
});



function Page() {
  const { t } = useI18n();
  const icons = [Brain, Droplets, Flame];
  const tags = ["GLP-1", "GIP", "Glucagon"];
  const accents = ["indigo", "indigo", "emerald"] as const;

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 md:py-20">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">
          {t.science.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {t.science.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{t.science.lead}</p>
      </div>

      <HeroImage
        {...scienceAbstractImage}
        alt="Abstract editorial illustration of clinical research and layered study phases"
        eager
      />


      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {t.science.m.map((m, i) => {
          const Icon = icons[i];
          const accent = accents[i];
          return (
            <motion.article
              key={tags[i]}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="card-elevated card-hover flex flex-col gap-4 p-6"
            >
              <div className="flex items-center justify-between">
                <span
                  className={`grid h-11 w-11 place-items-center rounded-xl ${
                    accent === "emerald" ? "bg-emerald/15 text-emerald" : "bg-indigo/15 text-indigo"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span className="font-mono text-xs text-muted-foreground">{tags[i]}</span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{m.title}</h2>
                <p className="mt-1 text-sm font-medium text-foreground/80">{m.lead}</p>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">{m.body}</p>
            </motion.article>
          );
        })}
      </div>

      <section className="mt-16 rounded-2xl border border-border bg-card p-6 md:p-10">
        <h2 className="font-display text-2xl text-foreground md:text-3xl">
          {t.science.combo.title}
        </h2>
        <p className="mt-4 max-w-3xl text-muted-foreground">{t.science.combo.body}</p>
      </section>

      <ConsultCta variant="section" />
      <RelatedLinks only={["/vergleich", "/studien", "/nebenwirkungen"]} />
    </div>
  );
}
